import { enUS } from "date-fns/locale";
import type { Locale } from "date-fns";

const locales: { [localeCode: string]: Locale } = {
  "@default": enUS,
};

export const getDefaultLocale = (): Locale => locales["@default"];

export const registerLocale = (locale: Locale) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  locales[locale.code!] = locale;
};

export const hasLoadedLocale = (localeCode: string) => {
  return typeof locales[localeCode] !== "undefined";
};

export const getLoadedLocale = (
  localeCode: string,
  regionlessFallback: boolean,
  defaultFallback: boolean,
): Locale => {
  const tryLocales: string[] = [localeCode];

  // aa-BB -> try just aa
  if (regionlessFallback) {
    const parts = localeCode.split(/[-_]/);
    if (parts.length && parts[0] !== localeCode) {
      tryLocales.push(parts[0]);

      if (parts[0] === "en") {
        // there is no 'en'
        tryLocales.push("en-US");
      }

      if (parts[0] === "ar") {
        // there is no 'ar'
        tryLocales.push("ar-SA");
      }
    }
  }

  if (defaultFallback) {
    tryLocales.push("@default");
  }

  const useLocale = tryLocales.filter((l) => hasLoadedLocale(l))[0];
  if (!useLocale) {
    throw new Error("Cannot use locale, it is not loaded");
  }

  return locales[useLocale];
};

export const loadLocaleFile = async (localeCode: string): Promise<{ default: Locale }> => {
  switch (localeCode) {
    case "af":
      return import("date-fns/locale/af");
    case "ar-DZ":
      return import("date-fns/locale/ar-DZ");
    case "ar-MA":
      return import("date-fns/locale/ar-MA");
    case "ar-SA":
      return import("date-fns/locale/ar-SA");
    case "az":
      return import("date-fns/locale/az");
    case "be":
      return import("date-fns/locale/be");
    case "bg":
      return import("date-fns/locale/bg");
    case "bn":
      return import("date-fns/locale/bn");
    case "ca":
      return import("date-fns/locale/ca");
    case "cs":
      return import("date-fns/locale/cs");
    case "cy":
      return import("date-fns/locale/cy");
    case "da":
      return import("date-fns/locale/da");
    case "de":
      return import("date-fns/locale/de");
    case "el":
      return import("date-fns/locale/el");
    case "en-AU":
      return import("date-fns/locale/en-AU");
    case "en-CA":
      return import("date-fns/locale/en-CA");
    case "en-GB":
      return import("date-fns/locale/en-GB");
    case "en-US":
      return import("date-fns/locale/en-US");
    case "eo":
      return import("date-fns/locale/eo");
    case "es":
      return import("date-fns/locale/es");
    case "et":
      return import("date-fns/locale/et");
    case "fa-IR":
      return import("date-fns/locale/fa-IR");
    case "fi":
      return import("date-fns/locale/fi");
    case "fr":
      return import("date-fns/locale/fr");
    case "fr-CA":
      return import("date-fns/locale/fr-CA");
    case "gl":
      return import("date-fns/locale/gl");
    case "gu":
      return import("date-fns/locale/gu");
    case "he":
      return import("date-fns/locale/he");
    case "hi":
      return import("date-fns/locale/hi");
    case "hr":
      return import("date-fns/locale/hr");
    case "hu":
      return import("date-fns/locale/hu");
    case "hy":
      return import("date-fns/locale/hy");
    case "id":
      return import("date-fns/locale/id");
    case "is":
      return import("date-fns/locale/is");
    case "it":
      return import("date-fns/locale/it");
    case "ja":
      return import("date-fns/locale/ja");
    case "ka":
      return import("date-fns/locale/ka");
    case "kk":
      return import("date-fns/locale/kk");
    case "ko":
      return import("date-fns/locale/ko");
    case "lt":
      return import("date-fns/locale/lt");
    case "lv":
      return import("date-fns/locale/lv");
    case "mk":
      return import("date-fns/locale/mk");
    case "ms":
      return import("date-fns/locale/ms");
    case "mt":
      return import("date-fns/locale/mt");
    case "nb":
      return import("date-fns/locale/nb");
    case "nl":
      return import("date-fns/locale/nl");
    case "nn":
      return import("date-fns/locale/nn");
    case "pl":
      return import("date-fns/locale/pl");
    case "pt":
      return import("date-fns/locale/pt");
    case "pt-BR":
      return import("date-fns/locale/pt-BR");
    case "ro":
      return import("date-fns/locale/ro");
    case "ru":
      return import("date-fns/locale/ru");
    case "sk":
      return import("date-fns/locale/sk");
    case "sl":
      return import("date-fns/locale/sl");
    case "sr":
      return import("date-fns/locale/sr");
    case "sr-Latn":
      return import("date-fns/locale/sr-Latn");
    case "sv":
      return import("date-fns/locale/sv");
    case "ta":
      return import("date-fns/locale/ta");
    case "te":
      return import("date-fns/locale/te");
    case "th":
      return import("date-fns/locale/th");
    case "tr":
      return import("date-fns/locale/tr");
    case "ug":
      return import("date-fns/locale/ug");
    case "uk":
      return import("date-fns/locale/uk");
    case "uz":
      return import("date-fns/locale/uz");
    case "vi":
      return import("date-fns/locale/vi");
    case "zh-CN":
      return import("date-fns/locale/zh-CN");
    case "zh-TW":
      return import("date-fns/locale/zh-TW");
    default:
      throw new Error("invalid locale");
  }
};

export const loadLocale = async (localeCode: string): Promise<Locale> => {
  return loadLocaleFile(localeCode).then((m) => {
    const locale = m["default"];
    registerLocale(locale);
    return locale;
  });
};
